import { useStyletron } from '@thanx/uikit/theme'
import lineChart from 'assets/images/home/img_chart.svg'
import chat from 'assets/images/home/img_chatBubble.svg'
import fileCheck from 'assets/images/home/img_fileCheck.svg'
import fileDollar from 'assets/images/home/img_fileDollar.svg'
import gift from 'assets/images/home/img_gift.svg'
import palette from 'assets/images/home/img_palette.svg'
import plane from 'assets/images/home/img_paperPlane.svg'
import star from 'assets/images/home/img_star.svg'
import { buildTranslate } from 'locales'
import { useState } from 'react'
import { Col, Row } from 'react-bootstrap-five'
import { Link } from 'react-router-dom'
import NewCampaignModal from 'scenes/CampaignCenter/Landing/NewCampaignModal'
import { useAbility } from 'utilities/ability'
import Button from './Button'
import { useFeedbackTiles } from './useFeedbackTiles'

const t = buildTranslate('home')

type ButtonT = {
  id: string
  text: string
  icon: string
  url?: string
  onClick?: () => void
}

export const RbacButtonRow = () => {
  const ability = useAbility()
  const [css] = useStyletron()
  // FR-65 label, destination, and read-tile visibility for the two feedback
  // tiles, shared with ButtonRow so the two rows can't diverge.
  const { showReadTile, readLabel, readUrl, reportLabel, reportUrl } =
    useFeedbackTiles()

  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false)
  const openNewCampaignModal = () => setShowNewCampaignModal(true)
  const closeNewCampaignModal = () => setShowNewCampaignModal(false)

  const buttons: ButtonT[] = []

  // Campaigns — view
  if (ability.can('access', 'AllCampaigns')) {
    buttons.push({
      id: 'reports',
      text: t('buttons.view_campaign'),
      icon: lineChart,
      url: '/thanx_campaigns',
    })
  }

  // Campaigns — manage (create)
  if (ability.can('manage', 'AllCampaigns')) {
    buttons.push({
      id: 'start',
      text: t('buttons.create_campaign'),
      icon: plane,
      onClick: openNewCampaignModal,
    })
  }

  // Digital experiences / CMS
  if (ability.can('access', 'CMS')) {
    buttons.push({
      id: 'update',
      text: t('buttons.update_digital'),
      icon: palette,
      url: '/brand_content',
    })
  }

  // Points performance — show based on ability alone; archived state
  // is handled on the Points page. No eager fetch to avoid 403s.
  if (ability.can('access', 'Points')) {
    buttons.push({
      id: 'points_performance',
      text: t('buttons.points_performance'),
      icon: star,
      url: '/points',
    })
  }

  // Reward performance
  if (ability.can('access', 'RedeemManager')) {
    buttons.push({
      id: 'reward_performance',
      text: t('buttons.reward_performance'),
      icon: gift,
      url: '/rewards',
    })
  }

  // Billing
  if (ability.can('access', 'Billing')) {
    buttons.push({
      id: 'billing',
      text: t('buttons.billing'),
      icon: fileDollar,
      url: '/billing',
    })
  }

  // Read tile → the RecoveryAI homepage (FR-65). Visibility lives in
  // useFeedbackTiles (`showReadTile`): shown wherever /recovery_ai resolves for
  // this viewer — the un-set-up marketing page is open to any viewer at a
  // flagged merchant (RAI FR-39), while the configured console keeps its
  // MerchantSettings gate so the tile never dead-ends to "/".
  if (showReadTile) {
    buttons.push({
      id: 'read_feedback',
      text: readLabel,
      icon: chat,
      url: readUrl,
    })
  }

  // Report tile → the Orders 👍/👎 page (FR-65). Gated on that page's own OR
  // gate: Feedback (Ratings & Reviews) unlocks Details, LocationReports unlocks
  // Overview, and the page renders if either holds. Mirrors the Operations
  // Center nav's order-feedback gate.
  const canOrderFeedback =
    ability.can('access', 'Feedback') ||
    ability.can('access', 'LocationReports')
  if (canOrderFeedback) {
    buttons.push({
      id: 'feedback_report',
      text: reportLabel,
      icon: fileCheck,
      url: reportUrl,
    })
  }

  if (buttons.length === 0) {
    return null
  }

  return (
    <>
      <Row className="mb-xxl g-3">
        {buttons.map(button => (
          <Col className="mb-xs" key={button.id} xs={12} sm={6} md={4} lg={3}>
            {button.url ? (
              <Link
                className={css({
                  textDecoration: 'none !important',
                })}
                to={button.url}
              >
                <Button text={button.text} icon={button.icon} />
              </Link>
            ) : (
              <Button
                text={button.text}
                icon={button.icon}
                onClick={button.onClick}
              />
            )}
          </Col>
        ))}
      </Row>
      <NewCampaignModal
        isOpen={showNewCampaignModal}
        onClose={closeNewCampaignModal}
      />
    </>
  )
}
